import { type Ref, type UnwrapRef, computed } from "vue";

import { type PaginationData } from "shared/components/Pagination";
import { INSTAGRAM_MONITOR_MAX_HASHTAG_COUNT } from "shared/constants/social";
import { type Maybe } from "shared/types";

import { type HashtagId, type HashtagWithId } from "./types";

export type UseHashtagContext = {
  hasHashtags: Ref<boolean>;
  hashtagCollection: Ref<UnwrapRef<Map<HashtagId, HashtagWithId>>>;
  hashtagIsCurrentView: Ref<boolean>;
  hashtagIsLoading: Ref<boolean>;
  hashtagPagination: Ref<PaginationData>;
  hashtagSelectedIds: Ref<Set<HashtagId>>;
  hashtagTotalCollection: Ref<UnwrapRef<Map<HashtagId, HashtagWithId>>>;
  organisationSocialIntegrationId: Ref<Maybe<string>>;
};
export type UseHashtagContextReturn = ReturnType<typeof useHashtagContext>;

export const HASHTAG_CONTEXT = Symbol("hashtagContext");

export function useHashtagContext(options: UseHashtagContext) {
  const {
    hasHashtags,
    hashtagCollection,
    hashtagIsCurrentView,
    hashtagIsLoading,
    hashtagPagination,
    hashtagSelectedIds,
    hashtagTotalCollection,
    organisationSocialIntegrationId,
  } = options;

  const filteredHashtagCollection = computed(
    () =>
      new Map(
        [...hashtagCollection.value].filter(
          ([, hashtag]) => !hashtag.archivedAt
        )
      )
  );

  const hashtagTotalCount = computed(() => hashtagTotalCollection.value.size);

  const hashtagNames = computed(
    () =>
      new Set(
        [...hashtagTotalCollection.value.values()]
          .filter(({ archivedAt }) => !archivedAt)
          .map(({ hashtag }) => hashtag)
      )
  );

  const hashtagExceeded = computed(
    () => hashtagTotalCount.value >= INSTAGRAM_MONITOR_MAX_HASHTAG_COUNT
  );

  const hashtagIsEnabled = computed(() => {
    if (
      !organisationSocialIntegrationId.value ||
      hashtagCollection.value.size === 0
    ) {
      return false;
    }

    return ![...hashtagCollection.value.values()].every(({ archivedAt }) =>
      Boolean(archivedAt)
    );
  });

  const hashtagCanBeAdded = computed(() =>
    Boolean(!hashtagExceeded.value && organisationSocialIntegrationId.value)
  );

  function hashtagIsSelected(id: HashtagId) {
    return hashtagSelectedIds.value.has(id);
  }

  return {
    filteredHashtagCollection,
    hasHashtags,
    hashtagCanBeAdded,
    hashtagCollection,
    hashtagExceeded,
    hashtagIsCurrentView,
    hashtagIsEnabled,
    hashtagIsLoading,
    hashtagIsSelected,
    hashtagNames,
    hashtagPagination,
    hashtagSelectedIds,
    hashtagTotalCollection,
    hashtagTotalCount,
  };
}
